import {
  Box,
  Stack,
  Table,
  TableCell,
  TableHead,
  TableRow,
  TableBody,
  Typography,
  TextField,
} from '@mui/material';
import {
  ConditionOperatorEnum,
  type InventoryResource,
  Provider,
  QuotaStatus,
  ResourceType,
  type LimitData,
} from '@repo/api-gw-sdk';
import { useCallback, useMemo, useState } from 'react';

import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { useUser } from '@/contexts/useUser';
import { useWorkspace } from '@/contexts/useWorkspace';
import { CloudProviders } from '@/data/inventory/data';
import { ResourceTypes } from '@/data/inventory/resourceTypes';
import theme from '@/theme';
import { fileSizeFormatter } from '@/utils/fileSizeFormatter';

import type { ConfigureProjectLimitsEditorState } from './configureProjectLimitsWizard';

import ButtonWithIcon from '../buttons/buttonWithIcon';
import { CircleImage } from '../shared/circleImage';

const SupportedObjectCountLimitResourceTypes: string[] = [
  ResourceType.AwsS3,
  ResourceType.GcpCloudStorageBucket,
];

const tableStyling = {
  borderTop: '1px solid var(--mui-palette-divider)',

  '& th': {
    textTransform: 'uppercase',
    borderRight: '1px solid var(--mui-palette-divider)',
    padding: 0,
    width: '20%',

    '& div': {
      fontWeight: 'normal',
      padding: '12px 16px',
      backgroundColor: 'var(--mui-palette-background-tableHeader)',
    },

    '&:first-of-type div': {
      marginLeft: 0,
      paddingLeft: '40px',
    },
  },
};

const tableRowStyling = {
  '& td': {
    borderRight: '1px solid var(--mui-palette-divider)',
    padding: '12px 16px',

    '&:first-of-type': {
      paddingX: '36px',
    },
  },
};

export const ConfigureProjectLimitsEditor = (
  props: StepProps<ConfigureProjectLimitsEditorState>
) => {
  const { isEmployee } = useUser();

  if (!isEmployee) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <Typography variant='h2' color='error'>
          You are not authorized to configure project limits.
        </Typography>
      </Box>
    );
  }

  return (
    <StepContainer
      hideStepper
      onBackClick={props.back}
      canGoNext={() => true}
      nextButtonText={'Save Changes'}
      backButtonText='Cancel'
      onNextClick={() => props.finish()}
    >
      <LimitEditor
        limits={props.wizardState.limits}
        setLimits={(limits) =>
          props.setWizardState({ ...props.wizardState, limits })
        }
        addedResourceOverrides={props.wizardState.addedResourceOverrides}
        setAddedResourceOverrides={(resourceOverrides) =>
          props.setWizardState({
            ...props.wizardState,
            addedResourceOverrides: resourceOverrides,
          })
        }
        removedResourceOverrides={props.wizardState.removedResourceOverrides}
        setRemovedResourceOverrides={(resourceOverrides) =>
          props.setWizardState({
            ...props.wizardState,
            removedResourceOverrides: resourceOverrides,
          })
        }
      />
    </StepContainer>
  );
};

const LimitEditor = ({
  limits,
  setLimits,
  addedResourceOverrides,
  setAddedResourceOverrides,
  removedResourceOverrides,
  setRemovedResourceOverrides,
}: {
  limits: Record<string, LimitData>;
  setLimits: (limits: Record<string, LimitData>) => void;
  addedResourceOverrides: string[];
  setAddedResourceOverrides: (resourceOverrides: string[]) => void;
  removedResourceOverrides: string[];
  setRemovedResourceOverrides: (resourceOverrides: string[]) => void;
}) => {
  const [originalLimits] = useState({ ...limits });
  const dal = useDAL();
  const { data: overriddenResources } = dal.inventory.list(
    { pageIndex: 0, pageSize: 25 },
    { id: 'id', desc: false },
    {
      property: 'quotaStatus',
      operator: ConditionOperatorEnum.In,
      value: [QuotaStatus.InQuotaOverridden],
    }
  );

  return (
    <Stack>
      <SubHeader text='Limit Per Resource Type' />
      <ConfigureLimitsTable
        originalLimits={originalLimits}
        limits={limits}
        setLimits={setLimits}
      />
      <SubHeader text='Resource Specific Overrides' />
      <ConfigureResourceOverrideTable
        overriddenResources={overriddenResources?.resources || []}
        setAddedResourceOverrides={setAddedResourceOverrides}
        setRemovedResourceOverrides={setRemovedResourceOverrides}
        removedResourceOverrides={removedResourceOverrides}
        addedResourceOverrides={addedResourceOverrides}
      />
    </Stack>
  );
};

const SubHeader = ({ text }: { text: string }) => {
  return (
    <Box margin='40px 40px 24px 40px'>
      <Typography variant='h3'>{text}</Typography>
    </Box>
  );
};

const ConfigureLimitsTable = ({
  originalLimits,
  limits,
  setLimits,
}: {
  originalLimits: Record<string, LimitData>;
  limits: Record<string, LimitData>;
  setLimits: (limits: Record<string, LimitData>) => void;
}) => {
  return (
    <Table>
      <TableHead>
        <TableRow sx={tableStyling}>
          <TableCell>
            <Box>
              <Typography variant='overline'>Resource Type</Typography>
            </Box>
          </TableCell>
          <TableCell>
            <Box>
              <Typography variant='overline'>Max Resource Limit</Typography>
            </Box>
          </TableCell>
          <TableCell>
            <Box>
              <Typography variant='overline'>Max Size Limit</Typography>
            </Box>
          </TableCell>
          <TableCell>
            <Box>
              <Typography variant='overline'>Max Object Count Limit</Typography>
            </Box>
          </TableCell>
        </TableRow>
      </TableHead>
      {originalLimits && (
        <TableBody>
          {Object.entries(ResourceTypes)
            .filter(
              ([, resourceTypeData]) =>
                resourceTypeData.provider !== Provider.ProviderUnspecified
            )
            .sort(
              (a, b) =>
                a[1].provider.localeCompare(b[1].provider) ||
                a[1].displayName.localeCompare(b[1].displayName)
            )
            .map(([resourceType, resourceTypeData]) => {
              const limit = limits[resourceType];
              const provider = resourceTypeData.provider;
              return (
                <TableRow key={resourceType} sx={tableRowStyling}>
                  <TableCell>
                    <Box>
                      <Stack direction='row' alignItems='center' gap='8px'>
                        <CircleImage
                          alt={CloudProviders[provider].displayName}
                          src={CloudProviders[provider].logo}
                        />
                        <Typography>
                          {ResourceTypes[resourceType].displayName}
                        </Typography>
                      </Stack>
                    </Box>
                  </TableCell>
                  <LimitCell
                    limit={limit?.quotaLimit}
                    onChange={(limit) => {
                      setLimits({
                        ...limits,
                        [resourceType]: {
                          ...limits[resourceType],
                          quotaLimit: limit,
                        },
                      });
                    }}
                    isEdited={
                      limits[resourceType]?.quotaLimit !==
                      originalLimits[resourceType]?.quotaLimit
                    }
                  />
                  <LimitCell
                    limit={limit?.sizeLimit}
                    onChange={(limit) => {
                      setLimits({
                        ...limits,
                        [resourceType]: {
                          ...limits[resourceType],
                          sizeLimit: limit,
                        },
                      });
                    }}
                    isEdited={
                      limits[resourceType]?.sizeLimit !==
                      originalLimits[resourceType]?.sizeLimit
                    }
                    convertToBytes
                  />
                  <LimitCell
                    limit={limit?.objectCountLimit}
                    onChange={(limit) => {
                      setLimits({
                        ...limits,
                        [resourceType]: {
                          ...limits[resourceType],
                          objectCountLimit: limit,
                        },
                      });
                    }}
                    isEdited={
                      limits[resourceType]?.objectCountLimit !==
                      originalLimits[resourceType]?.objectCountLimit
                    }
                    disabled={
                      !SupportedObjectCountLimitResourceTypes.includes(
                        resourceType
                      )
                    }
                  />
                </TableRow>
              );
            })}
        </TableBody>
      )}
    </Table>
  );
};

const ConfigureResourceOverrideTable = ({
  overriddenResources,
  setAddedResourceOverrides,
  setRemovedResourceOverrides,
  removedResourceOverrides,
  addedResourceOverrides,
}: {
  overriddenResources: InventoryResource[];
  setAddedResourceOverrides: (resourceOverrides: string[]) => void;
  setRemovedResourceOverrides: (resourceOverrides: string[]) => void;
  removedResourceOverrides: string[];
  addedResourceOverrides: string[];
}) => {
  const { snackbar } = useWorkspace();
  const dal = useDAL();
  const [newResourceId, setNewResourceId] = useState('');
  const [isLoadingNewResource, setIsLoadingNewResource] = useState(false);
  const [addedResources, setAddedResources] = useState<InventoryResource[]>([]);

  const allResources = useMemo<
    (InventoryResource & { isAdded: boolean })[]
  >(() => {
    return [
      ...overriddenResources.map((resource) => ({
        ...resource,
        isAdded: false,
      })),
      ...addedResources.map((resource) => ({ ...resource, isAdded: true })),
    ];
  }, [overriddenResources, addedResources]);

  return (
    <Table>
      <TableHead>
        <TableRow sx={tableStyling}>
          <TableCell>
            <Box>
              <Typography variant='overline'>Resource Type</Typography>
            </Box>
          </TableCell>
          <TableCell>
            <Box>
              <Typography variant='overline'>Resource ID</Typography>
            </Box>
          </TableCell>
          <TableCell>
            <Box>
              <Typography variant='overline'>Resource Name</Typography>
            </Box>
          </TableCell>
          <TableCell>
            <Box>
              <Typography variant='overline'>Actions</Typography>
            </Box>
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {allResources.map((resource) => {
          const provider = resource.cloudProvider;
          const resourceType = resource.resourceType;
          return (
            <TableRow
              key={resource.id}
              sx={{
                ...tableRowStyling,
                backgroundColor:
                  removedResourceOverrides.includes(resource.id) ||
                  resource.isAdded
                    ? 'var(--mui-palette-warning-bgLightHover)'
                    : 'transparent',
              }}
            >
              <TableCell>
                <Box>
                  <Stack direction='row' alignItems='center' gap='8px'>
                    <CircleImage
                      alt={CloudProviders[provider].displayName}
                      src={CloudProviders[provider].logo}
                    />
                    <Typography>
                      {ResourceTypes[resourceType].displayName}
                    </Typography>
                  </Stack>
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant='body1'>{resource.id}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant='body1'>{resource.resourceName}</Typography>
              </TableCell>
              <TableCell>
                {resource.isAdded ? (
                  <ButtonWithIcon
                    text='Remove New Resource Override'
                    icon='material-symbols-undo-rounded'
                    onClick={() => {
                      setAddedResources([
                        ...addedResources.filter((r) => r.id !== resource.id),
                      ]);
                      setAddedResourceOverrides([
                        ...addedResourceOverrides.filter(
                          (id) => id !== resource.id
                        ),
                      ]);
                    }}
                  />
                ) : removedResourceOverrides.includes(resource.id) ? (
                  <ButtonWithIcon
                    text='Readd Override'
                    icon='material-symbols-undo-rounded'
                    onClick={() => {
                      setRemovedResourceOverrides([
                        ...removedResourceOverrides.filter(
                          (id) => id !== resource.id
                        ),
                      ]);
                    }}
                  />
                ) : (
                  <ButtonWithIcon
                    text='Remove Override'
                    icon='material-symbols-delete-outline'
                    onClick={() => {
                      setRemovedResourceOverrides([
                        ...removedResourceOverrides,
                        resource.id,
                      ]);
                    }}
                  />
                )}
              </TableCell>
            </TableRow>
          );
        })}

        <TableRow id='Override new resource'>
          <TableCell />
          <TableCell>
            <TextField
              variant='filled'
              sx={{ width: '100%' }}
              label='New Resource ID'
              value={newResourceId}
              onChange={(e) => setNewResourceId(e.target.value)}
            />
          </TableCell>
          <TableCell />
          <TableCell>
            <ButtonWithIcon
              text='Override New Resource'
              icon='material-symbols-add-rounded'
              disabled={
                isLoadingNewResource ||
                newResourceId === '' ||
                !isValidUUID(newResourceId)
              }
              onClick={() => {
                if (!newResourceId) {
                  return;
                }
                if (!isValidUUID(newResourceId)) {
                  snackbar.showErrorMessage('Invalid resource ID');
                  return;
                }
                if (
                  allResources.some((resource) => resource.id === newResourceId)
                ) {
                  snackbar.showErrorMessage('Resource already overridden');
                  return;
                }
                setIsLoadingNewResource(true);
                dal.inventory
                  .getWithoutHook(newResourceId)
                  .then((resource) => {
                    setAddedResourceOverrides([
                      ...addedResourceOverrides,
                      resource.resource.id,
                    ]);
                    setAddedResources([...addedResources, resource.resource]);
                    setIsLoadingNewResource(false);
                  })
                  .catch((error) => {
                    if (error.code === 404) {
                      setIsLoadingNewResource(false);
                      snackbar.showErrorMessage('Resource not found');
                      return;
                    }
                    snackbar.showErrorMessage(error.message);
                  });
              }}
            />
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
};

const LimitCell = ({
  limit,
  onChange,
  convertToBytes = false,
  isEdited = false,
  disabled = false,
}: {
  limit: number | null | undefined;
  onChange: (limit: number | undefined) => void;
  convertToBytes?: boolean;
  isEdited?: boolean;
  disabled?: boolean;
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const text = useMemo(() => {
    if (convertToBytes && !isEditing && limit !== null && limit !== undefined) {
      return fileSizeFormatter(limit);
    }
    return limit;
  }, [limit, convertToBytes, isEditing]);

  const focusedRef = useCallback((node: HTMLInputElement | null) => {
    if (node) {
      node.focus();
      node.select();
    }
  }, []);

  return (
    <TableCell
      sx={
        isEdited
          ? { backgroundColor: 'var(--mui-palette-warning-bgLightHover)' }
          : disabled
            ? { backgroundColor: 'var(--mui-palette-background-disabled)' }
            : {}
      }
      onClick={() => !disabled && setIsEditing(true)}
    >
      <Box>
        {isEditing ? (
          <TextField
            sx={{
              width: '100%',
            }}
            inputProps={{
              style: {
                fontSize: theme().typography.body1.fontSize,
                padding: theme().typography.body1.padding || 0,
              },
            }}
            value={text ?? ''}
            type='number'
            variant='standard'
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              onChange(e.target.value !== '' ? +e.target.value : undefined);
            }}
            onBlur={() => {
              setIsEditing(false);
            }}
            inputRef={focusedRef}
          />
        ) : (
          <Typography variant='body1'>{text}</Typography>
        )}
      </Box>
    </TableCell>
  );
};

const isValidUUID = (id: string) => {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    id
  );
};
